import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  untracked,
  forwardRef,
  input,
  Input,
  Type,
  viewChild,
  inject
} from '@angular/core';
import { Router } from '@angular/router';
import { toObservable } from '@angular/core/rxjs-interop';
import { RowArgs, TableColumn, TableComponent, TableState, TableSelectionMode, SelectionSummary } from '@ui/aos-lib';
import { catchError, filter, map, Observable, switchMap } from 'rxjs';
import { SidePanelComponent } from '../../../layout/components/side-panel/side-panel.component';
import { CustomerDetails } from '../../../pages/csr/customers/customers.types';
import { DevicesApiService } from '../../../pages/equipment/devices/api/devices-api.service';
import { DeviceDetails } from '../../../pages/equipment/devices/components/devices.models';
import { SelectAllContextPayload, SelectionPayload } from '@portal/models/bulk-operations/selection.types';
import { LoaderService } from '../../../services/utility/loader.service';
import { ListingPageService } from '../listing-page/listing-page.service';
import { addAssociatedToEachDevice } from '@portal/models/device';
import { TranslateService } from 'src/app/services/translate/translate.service';
import { TimezoneService } from 'src/app/services/utility/timezone.service';
import { RouterStateService } from 'src/app/services/utility/router-state.service';
import { PortalPath } from 'src/app/routes/app.types';
import { ListingPageState } from '../listing-page/store/listing-page.state';
import { UserConfigService } from 'src/app/services/utility/user-config.service';
import { Store } from '@ngrx/store';
import { ListingPageActions } from '../listing-page/store/listing-page.actions';

const PAGE_SELECTION_COLUMN_WIDTH_PX = 30;

@Component({
  selector: 'aos-listing-table',
  imports: [AsyncPipe, TableComponent, forwardRef(() => SidePanelComponent)],
  templateUrl: './listing-table.component.html',
  styleUrl: './listing-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ListingTableComponent {
  readonly pageState = inject(ListingPageService);
  loaderService = inject(LoaderService);
  protected readonly devicesApiService = inject(DevicesApiService);
  protected readonly translate = inject(TranslateService);
  protected readonly timezoneService = inject(TimezoneService);
  protected readonly router = inject(Router);
  private routerState = inject(RouterStateService);
  private userConfigService = inject(UserConfigService);
  readonly store = inject(Store);

  protected readonly table = viewChild(TableComponent);
  gridColumns = input.required<TableColumn[]>();
  @Input() gridState!: TableState;
  @Input() selectionFieldName!: string;
  @Input() shouldShowAssociatedDevices!: boolean;
  @Input() isSidePanelDisabled = false;
  @Input() sidePanelTitleComponent!: Type<Component>;
  @Input() sidePanelBodyComponent!: Type<Component>;
  @Input() sidePanelTitle: string | null = null;
  @Input() parentId: string = '';
  @Input() tableSelectionMode: TableSelectionMode = TableSelectionMode.None;
  @Input() rowPrimaryKey: number | string = '';
  // Page-level select-all trigger: incrementing number triggers a select-all
  forceSelectAllTick = input<number>(0);
  // Guard against repeated triggering when the same tick value is re-set
  private lastForceSelectAllTick = 0;
  // Guard against repeated clear-global-all triggering when the same tick value is re-set
  private lastClearGlobalAllTick = 0;
  // Last server query (filters + search) captured right before the last select-all click
  protected lastQueryBeforeSelectAll: SelectAllContextPayload | null = null;

  protected titleComponentData!: [number, number];
  protected readonly gridData = this.pageState.gridData;

  protected readonly isSidePanelOpened = computed(() => this.pageState.pageState().sidePanelOpened, {
    equal: (a: boolean, b: boolean) => a === b
  });
  protected readonly selectedListingItemIndex = computed(() => this.pageState.pageState().selectedListingItemIndex, {
    equal: (a: ListingPageState['selectedListingItemIndex'], b: ListingPageState['selectedListingItemIndex']) => a === b
  });

  protected readonly selectedRowBasicData = computed(() => {
    const rowIndex = this.selectedListingItemIndex();
    if (rowIndex === null || rowIndex === undefined) {
      return null;
    }
    return this.gridData().data[rowIndex] ?? null;
  });

  protected readonly selectedRow$ = toObservable(this.selectedListingItemIndex).pipe(
    map(rowIndex => {
      if (rowIndex === null || rowIndex === undefined) {
        return null;
      }

      const row = this.gridData().data[rowIndex];
      const rawId = row ? row[this.selectionFieldName] : null;

      return rawId === null || rawId === undefined ? null : String(rawId);
    })
  );

  protected readonly deviceDetails$: Observable<DeviceDetails[] | null | unknown[] | unknown> = this.selectedRow$.pipe(
    filter(Boolean),
    switchMap((endpointID: string) => {
      return this.devicesApiService.getDeviceDetails(endpointID).pipe(
        map((details: DeviceDetails) => {
          const { longitude, latitude } = details.geoMetaData || { longitude: 0, latitude: 0 };
          this.titleComponentData = [longitude, latitude];
          return details ? [addAssociatedToEachDevice(this.timezoneService.getDetailsWithZonedDates(details))] : null;
        }),
        catchError(() => [])
      );
    })
  );

  readonly onPayloadChange = effect(() => {
    if (this.pageState.payload()) {
      // Read selection untracked so this effect only re-runs on payload changes
      // (paging/filter/sort/refresh), not when the user checks/unchecks rows.
      const selection = untracked(() => this.buildSelectionPayload());
      this.pageState.fetchGridData(selection);
    }
  });

  // Build the selection context (previously selected devices + prior "select all")
  // to send with the grid data request. Scoped to page-selection listings (DCU/metering).
  // `extraCheckedRows` lets callers include rows that have just been applied to the table
  // (e.g. a file-based selection) whose async selection summary has not yet updated the store.
  private buildSelectionPayload(extraCheckedRows: RowArgs[] | null = null): SelectionPayload | null {
    if (this.tableSelectionMode !== TableSelectionMode.Page) {
      return null;
    }

    if (!this.rowPrimaryKey) {
      return null;
    }

    const field = String(this.rowPrimaryKey);
    const toUniqueIDs = (rows: RowArgs[] | null): string[] =>
      (rows ?? []).reduce<string[]>((acc, row) => {
        const id = String((row?.dataItem as Record<string, unknown> | undefined)?.[field] ?? '');

        if (id) {
          acc.push(id);
        }
        return acc;
      }, []);

    const isLiveGlobalAll = this.pageState.checkedAllRows() === true;
    const uniqueIDs = isLiveGlobalAll
      ? []
      : Array.from(
          new Set([
            ...toUniqueIDs(this.pageState.checkedRows()),
            // Include rows applied via a file-based selection that are still pending (their async
            // selection summary has not updated checkedRows yet), so the very first fetch triggered
            // by the selection already carries them and the backend can compute their hidden count.
            ...toUniqueIDs(this.pageState.pendingCheckedRows()),
            ...toUniqueIDs(extraCheckedRows)
          ])
        );
    const excludedUniqueIDs = toUniqueIDs(this.pageState.checkedExcludedRows());
    // Prefer the local snapshot, but fall back to the service-held context so an active
    // saved select-all survives a table re-render/recreation (which clears the local field).
    const selectAllContext = this.lastQueryBeforeSelectAll ?? this.pageState.savedSelectAllQuery();
    const hasSelectAllContext = selectAllContext !== null;
    // A select-all stays active for the backend while its context is present, even after
    // a query change demotes the live header. It only turns off when the context is cleared.
    const isSelectAll = this.pageState.checkedAllRows() === true || hasSelectAllContext;

    // Nothing selected and no active select-all context: no selection context to send.
    if (!isSelectAll && uniqueIDs.length === 0 && excludedUniqueIDs.length === 0) {
      return null;
    }

    return {
      uniqueIDs,
      excludedUniqueIDs,
      isSelectAll,
      selectAllContext,
      uniqueUIDsOnPage: []
    };
  }

  // Watch the trigger input and call the internal table API when it changes
  readonly onForceSelectAll = effect(() => {
    const tick = this.forceSelectAllTick();
    // Only react when tick actually changes to a new value and is > 0
    if (tick > 0 && tick !== this.lastForceSelectAllTick) {
      this.lastForceSelectAllTick = tick;
      // Clear the table's existing selection FIRST: triggerGlobalSelectAll resets checkedRows
      // (via the emitted selection summary) and sets checkedAllRows. Only afterwards expose the
      // select-all context, so the payload is never built with the context active while stale
      // individual ids still linger in checkedRows.
      this.triggerGlobalSelectAll();
      this.captureLastQueryBeforeSelectAll();
      // A global select-all covers every row matching the current query, so the counts are known
      // in the UI (selected = grid total, hidden = 0). Set them locally instead of round-tripping
      // to the backend, which only recomputed the same values. onSeedSelectAllTotal then seeds the
      // table counter from the updated store total.
      const total = untracked(() => this.pageState.gridData().total);
      this.pageState.applySelectAll(total);
    }
  });

  // Watch the service trigger to clear the table's live global-all state (header + visible)
  // when a global "select all" is demoted into a committed/frozen selection on query change.
  readonly onClearGlobalAll = effect(() => {
    const tick = this.pageState.clearGlobalAllTick();
    if (tick > 0 && tick !== this.lastClearGlobalAllTick) {
      this.lastClearGlobalAllTick = tick;
      untracked(() => this.table()?.clearGlobalAll());
    }
  });

  // Persist the current filters/search so the server query can be rebuilt later
  // (without sorting or paging). Read untracked to avoid re-running the effect on state changes.
  private captureLastQueryBeforeSelectAll(): void {
    const payload = untracked(() => this.pageState.payload());
    const searchValue = payload?.search?.searchValue ?? '';
    const searchTypes = payload?.search?.searchTypes ? [...payload.search.searchTypes] : [];
    const hasSearchContext = Boolean(searchValue || searchTypes.length);
    this.lastQueryBeforeSelectAll = {
      filters: payload?.filters ? [...payload.filters] : [],
      search: hasSearchContext ? { searchValue, searchTypes } : null
    };
    // Expose the active select-all context to consumers that only see the service
    // (e.g. bulk actions), so they treat a saved select-all as "all rows checked".
    this.pageState.selectAllContextActive.set(true);
    // Also expose the captured query snapshot so consumers (bulk operations) target the
    // same rows that were counted/shown, instead of the current (possibly changed) query.
    this.pageState.savedSelectAllQuery.set(this.lastQueryBeforeSelectAll);
  }

  // Allow wrappers (e.g., DCU listing) to programmatically initialize checked rows.
  readonly onInitializeCheckedRowsRequest = effect(() => {
    const shouldApply = this.pageState.shouldApplyPendingCheckedRows();
    const table = this.table();

    if (!shouldApply || !table) {
      return;
    }

    untracked(() => {
      const rows = this.pageState.pendingCheckedRows();
      if (!rows || rows.length === 0) {
        return;
      }

      // The grid fetch triggered by the file selection already carried these rows in its
      // selection payload (see buildSelectionPayload), so gridData now holds the backend's
      // totalSelectedCount. Seed the counter with it and apply the checks in a single shot.
      const totalSelectedCount = this.pageState.gridData().totalSelectedCount ?? 0;
      table.initializeCheckedRows({ totalSelectedCount, selectedRows: rows });

      // Clear request to avoid re-applying on subsequent renders.
      this.pageState.markInitializeCheckedRowsApplied();
    });
  });

  // Reflect the backend's per-page checked rows (e.g. a still-matching frozen select-all)
  // as UI-only checkboxes, without adding them to the table selection or the total count.
  readonly onUiCheckedRows = effect(() => {
    const ids = this.pageState.uiCheckedRowIds();
    const table = this.table();

    if (!table || !ids || ids.length === 0) {
      return;
    }

    // The backend sends only the row ids; rebuild the minimal RowArgs the table needs,
    // keyed by the configured primary key, so `checkedRowsOnUI` can resolve each id.
    const field = String(this.rowPrimaryKey);
    const rows = ids.map((id, index) => ({ dataItem: { [field]: id }, index }) as RowArgs);

    // Defer so the table has rendered the freshly loaded page before we mark checkboxes.
    queueMicrotask(() => table.checkedRowsOnUI(rows));
  });

  // After a Select All the backend returns the authoritative totalSelectedCount. Seed the table
  // counter with it (0 rows to mark: the header/global state already paints the visible ones).
  readonly onSeedSelectAllTotal = effect(() => {
    const totalSelectedCount = this.pageState.totalSelectedCount();
    const table = this.table();

    if (!table) {
      return;
    }

    untracked(() => {
      if (this.pageState.selectAllContextActive()) {
        table.initializeCheckedRows({ totalSelectedCount, selectedRows: [] });
      }
    });
  });
  //todo: wtf? cant find where it gets initialized
  protected readonly customerDetails$!: Observable<CustomerDetails>;

  public onTableStateChange(tableState: TableState): void {
    this.pageState.onTableStateChange(tableState);
  }

  public selectionSummaryChange(event: SelectionSummary): void {
    // When the selection is fully cleared, drop the captured select-all query context
    // so the backend no longer receives a stale select-all. The table owns the count
    // and the previous select-all bookkeeping, so no other adjustment is needed here.
    if (!event.isGlobalAll && event.total === 0) {
      this.lastQueryBeforeSelectAll = null;
      this.pageState.selectAllContextActive.set(false);
      this.pageState.savedSelectAllQuery.set(null);
    }
    this.pageState.selectionSummaryChange(event.checkedItems, event.uncheckedItems, event.total, event.isGlobalAll);
  }

  public selectAllAllowedChange(allowed: boolean): void {
    this.pageState.selectAllVisibleChange(allowed);
  }

  public selectRow(row: RowArgs | undefined): void {
    const absoluteIndex = row?.index;
    const pageOffset = this.gridState?.skip ?? 0;
    const rowIndex = absoluteIndex === undefined ? undefined : absoluteIndex - pageOffset;

    if (rowIndex !== undefined && rowIndex < 0) {
      return;
    }
    // as listing table is commonly used across the app, we need to prevent opening side panel on some pages
    if (this.#isSidePanelDisabled() || rowIndex === this.selectedListingItemIndex()) {
      return;
    }
    this.pageState.selectListingItem({ rowIndex, sidePanelOpened: true });
  }

  public columnResize(event: Record<string, string | number>): void {
    const normalizedWidths = this.#normalizeColumnWidths(event);
    this.userConfigService.saveColumnWidth(this.pageState.model!.pageId, normalizedWidths);
    this.store.dispatch(
      ListingPageActions.columnsWidthChanged({
        pageId: this.pageState.model!.pageId,
        columnsWidth: normalizedWidths
      })
    );
  }

  #normalizeColumnWidths(event: Record<string, string | number>): Record<string, number> {
    const gridColumns = this.gridColumns();
    const eventKeys = Object.keys(event);
    const selectionOffset = this.tableSelectionMode === TableSelectionMode.Page ? PAGE_SELECTION_COLUMN_WIDTH_PX : 0;

    if (!this.#shouldNormalizeColumnWidths(event, gridColumns, selectionOffset)) {
      return event as Record<string, number>;
    }

    const hasSelectionPlaceholder = eventKeys.some(key => key.startsWith('column_'));

    return gridColumns.reduce<Record<string, number>>((acc, column, index) => {
      // Prefer field-based mapping; fallback to positional mapping only when the event includes synthetic columns.
      const fallbackIndex =
        this.tableSelectionMode === TableSelectionMode.Page && hasSelectionPlaceholder ? index + 1 : index;
      const fallbackKey = eventKeys[fallbackIndex];
      const renderedWidth = event[column.field] ?? (fallbackKey ? event[fallbackKey] : undefined);
      const parsedWidth = typeof renderedWidth === 'number' ? renderedWidth : Number(renderedWidth);
      const width = Number.isFinite(parsedWidth) ? parsedWidth : Number(column.width ?? 0);

      acc[column.field] = selectionOffset ? Math.max(width - selectionOffset, 0) : width;
      return acc;
    }, {});
  }

  #shouldNormalizeColumnWidths(
    event: Record<string, string | number>,
    gridColumns: TableColumn[],
    selectionOffset: number
  ): boolean {
    const eventKeys = Object.keys(event);

    return !(
      selectionOffset === 0 &&
      eventKeys.length === gridColumns.length &&
      eventKeys.every(key => gridColumns.some(column => column.field === key)) &&
      gridColumns.every(column => {
        const value = event[column.field];
        return typeof value === 'number' && Number.isFinite(value);
      })
    );
  }

  public columnReorder(event: Record<string, string | number>): void {
    this.userConfigService.saveColumnOrder(this.pageState.model!.pageId, event);
    this.store.dispatch(
      ListingPageActions.columnsOrderChanged({
        pageId: this.pageState.model!.pageId,
        columnsOrder: event
      })
    );
  }

  public resetSelectedRow(): void {
    this.pageState.selectListingItem({ rowIndex: null, sidePanelOpened: false });
    this.table()?.resetSelection();
  }

  public triggerGlobalSelectAll(): void {
    this.table()?.triggerGlobalSelectAll();
  }

  public triggerSelectionReset(): void {
    this.table()?.triggerSelectionReset();
  }

  #isSidePanelDisabled(): boolean {
    return (
      this.routerState.isPath(PortalPath.alarms) ||
      this.routerState.isPath(PortalPath.reportGroups) ||
      this.routerState.isPath(PortalPath.userRoles) ||
      this.routerState.isPath(PortalPath.subscriptionManagement) ||
      this.routerState.isPath(PortalPath.eventsConfiguration) ||
      this.routerState.isPath(PortalPath.history) ||
      this.routerState.isPath(PortalPath.basestationProvisioning)
    );
  }
}
